import { useApplyEdition } from '@/pages/Apply/components/use-apply-edition';
import { Link, router } from '@inertiajs/react';
import {
  ArrowRight,
  Check,
  Copy,
  Pencil,
  Plus,
  RotateCw,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SITE_URL } from '@/lib/constants';
import { toastFlash } from '@/lib/flash-toast';
import { cn } from '@/lib/utils';
import type { ApplyDashboardsShow } from '@/types';
import { errorMessages } from '../components/errors';
import { EventLabel } from '../components/EventLabel';
import { LeaveTeamButton } from '../components/LeaveTeamButton';
import { LogoutLink } from '../components/LogoutLink';

const MAX_INVITEES = 4;

// app/(public)/${eventSlug}/apply/dashboard/page.tsx + leader-dashboard.tsx.
export default function Show({ leader, modality, slots }: ApplyDashboardsShow) {
  const { eventSlug } = useApplyEdition();
  const [isPending, setIsPending] = useState(false);
  const [copiedSlot, setCopiedSlot] = useState<string | null>(null);
  const [editingSlot, setEditingSlot] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirm, setConfirm] = useState<null | {
    action: 'regenerate' | 'revoke';
    slotId: string;
    slotName: string;
  }>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');

  const inviteUrl = (slug: string, token: string) =>
    `${SITE_URL}/${eventSlug}/invite/${slug}_${token}`;

  const mutate = (
    run: (options: Parameters<typeof router.post>[2]) => void,
    fallback: string,
    onDone?: () => void,
  ) => {
    setIsPending(true);
    run({
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, fallback);
        onDone?.();
      },
      onError: (errors) => toast.error(errorMessages(errors).base ?? fallback),
      onFinish: () => setIsPending(false),
    });
  };

  const copyLink = async (slotId: string, slug: string, token: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(slug, token));
      setCopiedSlot(slotId);
      toast.success('Link copiado');
      setTimeout(() => setCopiedSlot(null), 2000);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo copiar el link');
    }
  };

  const regenerate = (slotId: string) =>
    mutate(
      (options) =>
        router.post(`/${eventSlug}/apply/slots/${slotId}/regenerate`, {}, options),
      'Link regenerado',
    );

  const startEdit = (slotId: string, currentName: string) => {
    setEditingSlot(slotId);
    setEditDraft(currentName);
  };

  const cancelEdit = () => {
    setEditingSlot(null);
    setEditDraft('');
  };

  const saveEdit = (slotId: string) => {
    const next = editDraft.trim();
    if (!next) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    mutate(
      (options) =>
        router.patch(
          `/${eventSlug}/apply/slots/${slotId}`,
          { placeholderName: next },
          options,
        ),
      'Nombre actualizado',
      cancelEdit,
    );
  };

  const revoke = (slotId: string) =>
    mutate(
      (options) => router.delete(`/${eventSlug}/apply/slots/${slotId}`, options),
      'Invitación revocada',
    );

  const submit = () =>
    mutate(
      (options) => router.post(`/${eventSlug}/apply/submit`, {}, options),
      '¡Postulación enviada!',
    );

  const addMember = () => {
    const name = newMemberName.trim();
    if (!name) {
      toast.error('Ingresa un nombre');
      return;
    }
    mutate(
      (options) =>
        router.post(`/${eventSlug}/apply/slots`, { placeholderName: name }, options),
      'Miembro agregado',
      () => {
        setNewMemberName('');
        setAddOpen(false);
      },
    );
  };

  const leaderDone = leader.profileCompletedAt !== null;
  const claimedCount = slots.filter((s) => s.claimed).length;
  const completedClaimed = slots.filter((s) => s.claimed?.completedAt).length;
  const allCompleted =
    leaderDone &&
    (modality === 'team_looking' ||
      (slots.length >= 2 && slots.every((s) => s.claimed?.completedAt)));

  return (
    <>
      <LogoutLink />
      <div className="mx-auto max-w-2xl space-y-8 px-4 py-12">
        <header className="space-y-2">
          <p className="text-muted-foreground text-sm">
            Postulación a <EventLabel />
          </p>
          <h1 className="font-bold text-2xl uppercase tracking-wide sm:text-3xl">
            Tu equipo
          </h1>
          <p className="text-muted-foreground text-sm">
            {modality === 'team_looking'
              ? 'Estás aplicando solo, buscando equipo.'
              : `${claimedCount}/${slots.length} hackers se sumaron · ${completedClaimed} completaron su perfil`}
          </p>
        </header>

        <section className="space-y-3">
          <div className="space-y-3">
            <MemberRow
              name={leader.fullName}
              handle={leader.githubHandle}
              status={leaderDone ? 'completed' : 'pending'}
              actionHref={`/${eventSlug}/apply/profile`}
              actionLabel={
                leaderDone ? 'Editar mi perfil' : 'Completar mi perfil'
              }
              leaderBadge
            />
            {modality !== 'team_looking' &&
              slots.map((slot) => {
                const status = slot.claimed
                  ? slot.claimed.completedAt
                    ? 'completed'
                    : 'in_progress'
                  : 'pending';
                return (
                  <div
                    key={slot.id}
                    className="space-y-3 border-2 border-primary bg-background/80 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {editingSlot === slot.id && !slot.claimed ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editDraft}
                              onChange={(e) => setEditDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit(slot.id);
                                else if (e.key === 'Escape') cancelEdit();
                              }}
                              disabled={isPending}
                              autoFocus
                              className="h-8"
                            />
                            <button
                              type="button"
                              onClick={() => saveEdit(slot.id)}
                              disabled={isPending}
                              className="border-2 border-primary p-1.5 text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
                              aria-label="Guardar"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={isPending}
                              className="border border-input p-1.5 hover:border-destructive hover:text-destructive disabled:opacity-50"
                              aria-label="Cancelar"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="truncate font-bold">
                              {slot.claimed?.fullName ?? slot.placeholderName}
                            </p>
                            {!slot.claimed && (
                              <button
                                type="button"
                                onClick={() =>
                                  startEdit(slot.id, slot.placeholderName)
                                }
                                disabled={isPending}
                                className="text-muted-foreground hover:text-primary disabled:opacity-50"
                                aria-label="Editar nombre"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                        {editingSlot !== slot.id && (
                          <p className="text-muted-foreground text-xs">
                            {slot.claimed ? (
                              <>@{slot.claimed.githubHandle}</>
                            ) : (
                              'esperando que se sume'
                            )}
                          </p>
                        )}
                      </div>
                      {editingSlot !== slot.id && (
                        <StatusBadge status={status} />
                      )}
                    </div>

                    {!slot.claimed && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <code className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded border border-input bg-background px-2 py-1 font-mono text-xs">
                            {inviteUrl(slot.nameSlug, slot.token)}
                          </code>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirm({
                                action: 'regenerate',
                                slotId: slot.id,
                                slotName: slot.placeholderName,
                              })
                            }
                            disabled={isPending}
                            className="border border-input p-2 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
                            aria-label="Regenerar link"
                            title="Regenerar link"
                          >
                            <RotateCw className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirm({
                                action: 'revoke',
                                slotId: slot.id,
                                slotName: slot.placeholderName,
                              })
                            }
                            disabled={isPending}
                            className="border border-input p-2 text-muted-foreground hover:border-destructive hover:text-destructive disabled:opacity-50"
                            aria-label="Revocar invitación"
                            title="Revocar invitación"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <Button
                          onClick={() =>
                            copyLink(slot.id, slot.nameSlug, slot.token)
                          }
                          disabled={isPending}
                          className="h-11 w-full text-sm uppercase tracking-wide"
                        >
                          {copiedSlot === slot.id ? (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Link copiado
                            </>
                          ) : (
                            <>
                              <Copy className="mr-2 h-4 w-4" />
                              Copiar invitación
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}

            {modality !== 'team_looking' &&
              slots.filter((s) => !s.revokedAt).length < MAX_INVITEES && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddOpen(true)}
                  disabled={isPending}
                  className="w-full border-dashed"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar hacker
                </Button>
              )}
          </div>
        </section>

        <section className="space-y-3 border-2 border-primary bg-background/80 p-6">
          <h2 className="font-bold text-sm uppercase tracking-wide">Enviar</h2>
          <p className="text-muted-foreground text-sm">
            {allCompleted
              ? modality === 'team_looking'
                ? 'Completaste tu perfil. Puedes enviar la postulación.'
                : 'Todos completaron su perfil. Puedes enviar la postulación.'
              : modality === 'team_looking'
                ? 'Cuando completes tu perfil, vas a poder enviar la postulación desde aquí.'
                : 'Cuando todos completen su perfil, vas a poder enviar la postulación desde aquí.'}
          </p>
          <Button
            onClick={submit}
            disabled={!allCompleted || isPending}
            className="w-full"
          >
            {isPending
              ? 'Enviando...'
              : allCompleted
                ? 'Enviar postulación'
                : modality === 'team_looking'
                  ? 'Esperando tu perfil...'
                  : 'Esperando equipo...'}
          </Button>
        </section>

        <div className="flex flex-col items-center gap-3 text-center text-muted-foreground text-xs">
          <Link href={`/${eventSlug}/apply/team-setup`} className="hover:underline">
            Editar configuración del equipo
          </Link>
          <LeaveTeamButton
            viewerRole="leader"
            willTransfer={slots.some((s) => s.claimed)}
          />
        </div>

        <Dialog
          open={addOpen}
          onOpenChange={(open) => {
            setAddOpen(open);
            if (!open) setNewMemberName('');
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar hacker al equipo</DialogTitle>
              <DialogDescription>
                Pon el nombre con el que vas a identificar la invitación. Tu
                compañero podrá corregirlo al sumarse.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="new-member-name">Nombre</Label>
              <Input
                id="new-member-name"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addMember();
                }}
                placeholder="ej. Rodrigo Pérez"
                disabled={isPending}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setAddOpen(false)}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <Button onClick={addMember} disabled={isPending}>
                {isPending ? 'Agregando...' : 'Agregar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={confirm !== null}
          onOpenChange={(open) => {
            if (!open) setConfirm(null);
          }}
        >
          <AlertDialogContent>
            {confirm?.action === 'regenerate' && (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Regenerar el link</AlertDialogTitle>
                  <AlertDialogDescription>
                    El link actual para <strong>{confirm.slotName}</strong>{' '}
                    dejará de funcionar. Si ya lo enviaste, tendrás que mandar
                    el nuevo.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isPending}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isPending}
                    onClick={() => {
                      const slotId = confirm.slotId;
                      setConfirm(null);
                      regenerate(slotId);
                    }}
                  >
                    Regenerar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )}
            {confirm?.action === 'revoke' && (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revocar la invitación</AlertDialogTitle>
                  <AlertDialogDescription>
                    La invitación a <strong>{confirm.slotName}</strong> se
                    eliminará y nadie podrá usarla. Esta acción no se puede
                    deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isPending}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isPending}
                    onClick={() => {
                      const slotId = confirm.slotId;
                      setConfirm(null);
                      revoke(slotId);
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Revocar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}

function MemberRow({
  name,
  handle,
  status,
  actionHref,
  actionLabel,
  leaderBadge,
}: {
  name: string;
  handle: string;
  status: 'pending' | 'in_progress' | 'completed';
  actionHref?: string;
  actionLabel?: string;
  leaderBadge?: boolean;
}) {
  const emphasize = status === 'pending' && !!actionHref;
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-2 bg-background/80 p-4 sm:flex-row sm:items-center sm:justify-between',
        emphasize
          ? 'border-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]'
          : 'border-primary',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-bold">{name}</p>
          {leaderBadge && (
            <span className="inline-flex items-center border border-primary px-1.5 py-0.5 font-bold text-[10px] text-primary uppercase tracking-wide">
              Líder
            </span>
          )}
        </div>
        <p className="text-muted-foreground text-xs">@{handle}</p>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={status} />
        {actionHref && actionLabel && (
          <Link
            href={actionHref}
            className={cn(
              'inline-flex items-center gap-1.5 font-bold uppercase tracking-wide transition-colors',
              emphasize
                ? 'animate-pulse border-2 border-primary bg-primary px-3 py-1.5 text-primary-foreground text-xs hover:animate-none hover:bg-primary/90'
                : 'border border-input px-3 py-1.5 text-xs hover:border-primary',
            )}
          >
            {actionLabel}
            {emphasize && <ArrowRight className="h-3.5 w-3.5" />}
          </Link>
        )}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: 'pending' | 'in_progress' | 'completed';
}) {
  const label =
    status === 'completed'
      ? 'Listo'
      : status === 'in_progress'
        ? 'En proceso'
        : 'Pendiente';
  return (
    <span
      className={cn(
        'inline-flex items-center border px-2 py-0.5 text-xs uppercase tracking-wide',
        status === 'completed' && 'border-primary bg-primary/10 text-primary',
        status === 'in_progress' && 'border-input text-muted-foreground',
        status === 'pending' && 'border-input text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}
